import { z } from 'zod';
import { COACH_ACTIONS, COACH_SURVEYS, type CoachActionDefinition } from './actions';
import { COACH_ANCHORS } from './anchors';
import { COACH_EVENTS } from './events';
import { COACH_CONTENT_TYPES, COACH_FORMS } from './forms';
import { COACH_GATES, CoachJourneySchema } from './schema';
import { COACH_SLOTS } from './slots';
import { COACH_LIBRARY_GROUPS, COACH_TEMPLATES } from './templates';

/**
 * Everything a journey can be built from, in one plain object: what to give an author (a
 * person, or a model writing a journey) alongside `coachJourneyJsonSchema()`. Every name a
 * journey uses comes from here, and `parseJourney` rejects any that does not.
 */
export function coachCatalog() {
  const jsonSchema = (schema: z.ZodType) =>
    z.toJSONSchema(schema, { io: 'input', unrepresentable: 'any' });
  return {
    actions: Object.fromEntries(
      Object.entries(COACH_ACTIONS).map(([id, definition]: [string, CoachActionDefinition]) => [
        id,
        {
          label: definition.label,
          params: jsonSchema(definition.params),
          refs: definition.refs,
          returns: definition.returns,
        },
      ])
    ),
    events: COACH_EVENTS,
    anchors: COACH_ANCHORS,
    slots: COACH_SLOTS,
    forms: COACH_FORMS,
    contentTypes: COACH_CONTENT_TYPES,
    gates: COACH_GATES,
    templates: COACH_TEMPLATES,
    libraryGroups: COACH_LIBRARY_GROUPS,
    surveys: COACH_SURVEYS,
  };
}

/**
 * The shape of a journey as JSON Schema (draft 2020-12), for tools and models that write
 * journeys. It describes the shape; the names and cross-checks (an action's own params, steps
 * that exist) are `parseJourney`'s, so always parse what was written.
 */
export function coachJourneyJsonSchema() {
  return z.toJSONSchema(CoachJourneySchema, { io: 'input', unrepresentable: 'any' });
}
